import { stepStandardVTCMedicalBenefitsTugoLookup as expenseLookup } from './claimCodeMapper';
import { MEDICAL_EXPENSE_TYPES } from '../Tugo.StandardVTCMedicalBenefits/constants';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire() {
  return [];
}

// Generic function to map any type of expense
function mapExpensesByType(data, itemsKey, expenseLookupKey, condition = true) {
  const itemsList = data[itemsKey];
  const itemsLength = itemsList && (itemsList.size !== undefined ? itemsList.size : itemsList.length);
  if (!condition || !itemsList || itemsLength < 1) {
    return [];
  }

  if (!Object.prototype.hasOwnProperty.call(expenseLookup, expenseLookupKey)) {
    return [];
  }

  const lookup = expenseLookup[expenseLookupKey];
  const claimCode = lookup && lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0
    ? lookup.associatedClaimCodes[0]
    : (lookup.claimCode || null);

  if (!claimCode) {
    return [];
  }

  const expenses = [];

  data[itemsKey].forEach((item) => {
    const expense = mapExpenseItem(item, claimCode);
    if (!!expense) {
      expenses.push(expense);
    }
  });

  return expenses;
}

function mapToExpenses(data) {
  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  const expenseConfigs = [
    // Doctor's Visit expenses
    {
      items: 'infoTelemedicineItems',
      lookup: 'telemedicine',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitTelemedicine === 'true',
    },
    {
      items: 'infoOutpatientPhysicianFeesItems',
      lookup: 'outpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true',
    },
    {
      items: 'infoFollowUpVisitItems',
      lookup: 'followUpVisit',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
    },
    {
      items: 'infoFollowUpFractureItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true',
    },
    // Diagnostic Tests
    {
      items: 'infoDiagnosticTestsItems',
      lookup: 'diagnosticTests',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS),
    },
    // Prescription Drugs
    {
      items: 'infoPrescriptionDrugsItems',
      lookup: 'prescriptionDrugs',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
    },
    // Ambulance Services
    {
      items: 'infoGroundAmbulanceItems',
      lookup: 'groundAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'true',
    },
    {
      items: 'infoAirAmbulanceItems',
      lookup: 'airAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'false' && data.ambulanceAirService === 'true',
    },
    {
      items: 'infoOtherAmbulanceItems',
      lookup: 'otherAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'false' && data.ambulanceAirService === 'false',
    },
    // Hospitalization
    {
      items: 'infoHospitalFeesItems',
      lookup: 'hospitalFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalFees === true,
    },
    {
      items: 'infoInpatientPhysicianFeesItems',
      lookup: 'inpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationInpatientPhysicianFees === true,
    },
    {
      items: 'infoXrayLabsDiagnosticsItems',
      lookup: 'xrayLabsDiagnostics',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationXrayLabsDiagnostics === true,
    },
    {
      items: 'infoEmergencyAirTransportationItems',
      lookup: 'emergencyAirTransportation',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationEmergencyAirTransportation === true,
    },
    {
      items: 'infoAirfareReturnHomeItems',
      lookup: 'airfareReturnHome',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationAirfareReturnHome === true,
    },
    {
      items: 'infoFamilyTransportationItems',
      lookup: 'familyTransportation',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportation === true,
    },
    {
      items: 'infoFamilyTransportationMealsAccommodationItems',
      lookup: 'familyTransportationMealsAccommodation',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportationMealsAccommodation === true,
    },
    {
      items: 'infoMedicalAttendantItems',
      lookup: 'medicalAttendant',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMedicalAttendant === true,
    },
    {
      items: 'infoReturnOfTravellingCompanionItems',
      lookup: 'returnOfTravellingCompanion',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationReturnOfTravellingCompanion === true,
    },
    {
      items: 'infoReturnOfDependentChildrenItems',
      lookup: 'returnOfDependentChildren',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationReturnOfDependentChildren === true,
    },
    {
      items: 'infoHospitalAllowanceItems',
      lookup: 'hospitalAllowance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalAllowance === true,
    },
    {
      items: 'infoOutOfPocketExpensesItems',
      lookup: 'outOfPocketExpenses',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOutOfPocketExpenses === true,
    },
    {
      items: 'infoChildcareItems',
      lookup: 'childcare',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationChildcare === true,
    },
    {
      items: 'infoReturnOfVehicleItems',
      lookup: 'returnOfVehicle',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationReturnOfVehicle === true,
    },
    {
      items: 'infoOtherHospitalizationItems',
      lookup: 'otherHospitalization',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === true,
    },
    // Essential Medical Equipment
    {
      items: 'infoEssentialMedicalEquipmentItems',
      lookup: 'essentialMedicalEquipment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
    },
    // Private Duty Nursing
    {
      items: 'infoPrivateDutyNursingItems',
      lookup: 'privateDutyNursing',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
    },
    // Fracture Treatment (standalone)
    {
      items: 'infoFractureTreatmentItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT),
    },
  ];

  const allExpenses = expenseConfigs.flatMap((config) =>
    mapExpensesByType(data, config.items, config.lookup, config.condition)
  );

  return [...allExpenses];
}

function mapToClaimExpensesLevel(data, claimTypes, selectedBenefits) {
  const selected =
    !!selectedBenefits && typeof selectedBenefits.toJS === 'function'
      ? selectedBenefits.toJS()
      : selectedBenefits || [];
  const claimCodeList = expenseLookup.claimCodeList;
  const list = [];
  if (selected.length > 0 && !selected.some((sel) => claimCodeList.includes(sel.claimCode))) {
    return list;
  }

  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  if (expensesSelected.length < 1 || claimTypes === undefined) {
    return list;
  }

  const medicalExpenses = mapToExpenses(data);

  const getClaimCodeFromLookup = (lookupKey) => {
    const lookup = expenseLookup[lookupKey];
    return lookup && lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0
      ? lookup.associatedClaimCodes[0]
      : null;
  };

  const lookupKeyToConfig = {
    // Doctor's Visit - Telemedicine (telephone or video call)
    telemedicine: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitTelemedicine === 'true',
      getDescription: () => 'Telemedicine Benefit',
    },
    // Doctor's Visit - First Visit (Outpatient Physician Fees)
    outpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true',
      getDescription: () => 'Outpatient Physician Fees',
    },
    // Doctor's Visit - Follow-up Visit
    followUpVisit: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
      getDescription: () => 'Follow-up visits',
    },
    // Doctor's Visit - Fracture Treatment (also used by standalone Fracture Treatment checkbox)
    fractureTreatment: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT, MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT],
      condition: () => (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true') || expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT),
      getDescription: () => 'Fracture Treatment',
    },
    // Diagnostic Tests
    diagnosticTests: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS),
      getDescription: () => 'Diagnostic Tests',
    },
    // Prescription Drugs
    prescriptionDrugs: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
      getDescription: () => 'Prescription Drugs',
    },
    // Ambulance Services - Ground
    groundAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'true',
      getDescription: () => 'Ground Ambulance',
    },
    // Ambulance Services - Air
    airAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'false' && data.ambulanceAirService === 'true',
      getDescription: () => 'Air Ambulance',
    },
    // Ambulance Services - Other
    otherAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'false' && data.ambulanceAirService === 'false',
      getDescription: () => 'Other Types of Ambulances',
    },
    // Hospitalization
    hospitalFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalFees === true,
      getDescription: () => 'Hospital Fees',
    },
    inpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationInpatientPhysicianFees === true,
      getDescription: () => 'Physician Fees while hospitalized',
    },
    xrayLabsDiagnostics: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationXrayLabsDiagnostics === true,
      getDescription: () => 'X-Ray, Labs, or other diagnostics while hospitalized',
      documentClaimCodeLookupKey: 'hospitalFees',
    },
    emergencyAirTransportation: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationEmergencyAirTransportation === true,
      getDescription: () => 'Emergency Air Transportation',
    },
    airfareReturnHome: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationAirfareReturnHome === true,
      getDescription: () => 'Airfare to Return Home For Treatment',
    },
    familyTransportation: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportation === true,
      getDescription: () => 'Family Transportation',
    },
    familyTransportationMealsAccommodation: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportationMealsAccommodation === true,
      getDescription: () => 'Family Transportation Meals and Accommodation',
    },
    medicalAttendant: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMedicalAttendant === true,
      getDescription: () => 'Medical Attendant',
    },
    returnOfTravellingCompanion: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationReturnOfTravellingCompanion === true,
      getDescription: () => 'Return of Travelling Companion',
    },
    returnOfDependentChildren: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationReturnOfDependentChildren === true,
      getDescription: () => 'Return of Dependent Children',
    },
    hospitalAllowance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalAllowance === true,
      getDescription: () => 'Hospital Allowance',
    },
    outOfPocketExpenses: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOutOfPocketExpenses === true,
      getDescription: () => 'Out-Of-Pocket Expenses',
    },
    childcare: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationChildcare === true,
      getDescription: () => 'Childcare',
    },
    returnOfVehicle: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationReturnOfVehicle === true,
      getDescription: () => 'Return of Vehicle',
    },
    otherHospitalization: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === true,
      getDescription: () => 'Other expenses related to hospitalization',
    },
    // Essential Medical Equipment
    essentialMedicalEquipment: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
      getDescription: () => 'Essential Medical Equipment',
    },
    // Private Duty Nursing
    privateDutyNursing: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
      getDescription: () => 'Private Duty Nursing',
    },
  };

  const expenseConfigsForMapping = [
    { items: 'infoTelemedicineItems', lookup: 'telemedicine' },
    { items: 'infoOutpatientPhysicianFeesItems', lookup: 'outpatientPhysicianFees' },
    { items: 'infoFollowUpVisitItems', lookup: 'followUpVisit' },
    { items: 'infoFollowUpFractureItems', lookup: 'fractureTreatment' },
    { items: 'infoDiagnosticTestsItems', lookup: 'diagnosticTests' },
    { items: 'infoPrescriptionDrugsItems', lookup: 'prescriptionDrugs' },
    { items: 'infoGroundAmbulanceItems', lookup: 'groundAmbulance' },
    { items: 'infoAirAmbulanceItems', lookup: 'airAmbulance' },
    { items: 'infoOtherAmbulanceItems', lookup: 'otherAmbulance' },
    // Hospitalization
    { items: 'infoHospitalFeesItems', lookup: 'hospitalFees' },
    { items: 'infoInpatientPhysicianFeesItems', lookup: 'inpatientPhysicianFees' },
    { items: 'infoXrayLabsDiagnosticsItems', lookup: 'xrayLabsDiagnostics' },
    { items: 'infoEmergencyAirTransportationItems', lookup: 'emergencyAirTransportation' },
    { items: 'infoAirfareReturnHomeItems', lookup: 'airfareReturnHome' },
    { items: 'infoFamilyTransportationItems', lookup: 'familyTransportation' },
    { items: 'infoFamilyTransportationMealsAccommodationItems', lookup: 'familyTransportationMealsAccommodation' },
    { items: 'infoMedicalAttendantItems', lookup: 'medicalAttendant' },
    { items: 'infoReturnOfTravellingCompanionItems', lookup: 'returnOfTravellingCompanion' },
    { items: 'infoReturnOfDependentChildrenItems', lookup: 'returnOfDependentChildren' },
    { items: 'infoHospitalAllowanceItems', lookup: 'hospitalAllowance' },
    { items: 'infoOutOfPocketExpensesItems', lookup: 'outOfPocketExpenses' },
    { items: 'infoChildcareItems', lookup: 'childcare' },
    { items: 'infoReturnOfVehicleItems', lookup: 'returnOfVehicle' },
    { items: 'infoOtherHospitalizationItems', lookup: 'otherHospitalization' },
    // Essential Medical Equipment, Private Duty Nursing, Fracture Treatment (standalone)
    { items: 'infoEssentialMedicalEquipmentItems', lookup: 'essentialMedicalEquipment' },
    { items: 'infoPrivateDutyNursingItems', lookup: 'privateDutyNursing' },
    { items: 'infoFractureTreatmentItems', lookup: 'fractureTreatment' },
  ];

  const lookupKeyToItemsKey = {};
  expenseConfigsForMapping.forEach((config) => {
    lookupKeyToItemsKey[config.lookup] = config.items;
  });

  const documentSectionsByClaimCode = new Map();

  Object.keys(lookupKeyToConfig).forEach((lookupKey) => {
    const claimCode = getClaimCodeFromLookup(lookupKey);
    const config = lookupKeyToConfig[lookupKey];
    const documentClaimCode = config.documentClaimCodeLookupKey
      ? getClaimCodeFromLookup(config.documentClaimCodeLookupKey)
      : claimCode;
    if (!documentClaimCode) return;
    if (!config.condition || !config.condition(claimCode)) return;

    const matchingExpenseType = config.expenseTypes.find((expenseType) =>
      expensesSelected.includes(expenseType)
    );
    if (!matchingExpenseType) return;

    const description =
      (config.getDocumentDescription && config.getDocumentDescription()) ||
      (config.getDescription && config.getDescription()) ||
      matchingExpenseType;
    const itemsKey = lookupKeyToItemsKey[lookupKey];
    const itemsData = itemsKey && data[itemsKey];
    const itemsDataLength = itemsData && (itemsData.size !== undefined ? itemsData.size : itemsData.length);
    const lookupExpenses =
      itemsKey && itemsDataLength > 0
        ? mapExpensesByType(data, itemsKey, lookupKey, true)
        : medicalExpenses.filter((expense) => expense.claimTypeCode === claimCode);

    if (!documentSectionsByClaimCode.has(documentClaimCode)) {
      documentSectionsByClaimCode.set(documentClaimCode, {
        description,
        expenses: [],
        lookupKeys: [],
      });
    }
    const entry = documentSectionsByClaimCode.get(documentClaimCode);
    entry.expenses.push(...lookupExpenses);
    entry.lookupKeys.push(lookupKey);
  });

  documentSectionsByClaimCode.forEach((entry, documentClaimCode) => {
    const claimTypeItem =
      claimTypes.find((item) => item.claimCode === documentClaimCode) ||
      entry.lookupKeys
        .map((key) => claimTypes.find((item) => item.claimCode === getClaimCodeFromLookup(key)))
        .find(Boolean);
    if (claimTypeItem) {
      const benefit = mapClaimExpensesLevelItem(
        claimTypeItem,
        entry.description,
        entry.expenses
      );
      benefit.claimCode = documentClaimCode;
      list.push(benefit);
    }
  });

  return list;
}

function mapExpenseItem(item, claimCode) {
  const plainItem = (item && typeof item.toJS === 'function') ? item.toJS() : item;
  if (!plainItem || !plainItem.amountClaimed || !plainItem.currency) {
    return null;
  }
  const selectedClaimForArray = Array.isArray(plainItem.selectedClaimFor)
    ? plainItem.selectedClaimFor
    : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  return {
    shortDescription: `${plainItem.descriptionOfItem}`,
    dateOfExpense: plainItem.dateOfExpenses,
    clientCost: plainItem.amountClaimed,
    currency: plainItem.currency,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
  };
}

function mapClaimExpensesLevelItem(claimType, description, expenses) {
  return {
    claimCode: claimType.claimCode,
    description,
    claimTypeId: claimType.claimTypeId,
    requiredDocuments: claimType.requiredDocuments,
    order: null,
    expenses: expenses || [],
  };
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
  mapToClaimExpensesLevel,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepStandardVTCMedicalBenefitsTugoDataMapper.js